// ============================================================
// FILE: src/App.jsx
// PURPOSE: Root component — sets up routing and auth guard
// ============================================================

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';

// ── Staff Pages ───────────────────────────────────────────
import LoginPage         from './pages/Login/LoginPage';
import Dashboard         from './pages/Dashboard/Dashboard';
import ClientsPage       from './pages/Clients/ClientsPage';
import ClientDetail      from './pages/Clients/ClientDetail';
import CaregiversPage    from './pages/Caregivers/CaregiversPage';
import VisitsPage        from './pages/Visits/VisitsPage';
import AttendancePage    from './pages/Attendance/AttendancePage';
import BillingPage       from './pages/Billing/BillingPage';
import CarePlansPage     from './pages/CarePlans/CarePlansPage';
import PayrollPage       from './pages/Payroll/PayrollPage';
import TrainingPage      from './pages/Training/TrainingPage';
import DocumentsPage     from './pages/Documents/DocumentsPage';
import MedicaidPage      from './pages/Medicaid/MedicaidPage';
import UsersPage         from './pages/Users/UsersPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';

// ── Client Portal Pages ───────────────────────────────────
import ClientDashboard   from './pages/ClientPortal/ClientDashboard';
import ClientVisits      from './pages/ClientPortal/ClientVisits';
import ClientCaregivers  from './pages/ClientPortal/ClientCaregivers';
import ClientAttendance  from './pages/ClientPortal/ClientAttendance';
import ClientCarePlan    from './pages/ClientPortal/ClientCarePlan';

// ── Protected Route ───────────────────────────────────────
// Just checks if logged in — Layout handles role-based content
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading">Loading...</div>;
  return user ? children : <Navigate to="/login" replace />;
};

// ── App Routes ────────────────────────────────────────────
const AppRoutes = () => {
  const { user } = useAuth();

  // Determine correct home path based on role
  const homePath = (user?.role || user?.Role) === 'Client'
    ? '/client/dashboard'
    : '/';

  return (
    <Routes>

      {/* Public route — redirect logged-in users to their home */}
      <Route
        path="/login"
        element={user ? <Navigate to={homePath} replace /> : <LoginPage />}
      />

      {/* All protected routes share the same Layout (sidebar + topbar) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* ── Staff / Admin routes ── */}
        <Route index                element={<Dashboard />} />
        <Route path="clients"       element={<ClientsPage />} />
        <Route path="clients/:id"   element={<ClientDetail />} />
        <Route path="caregivers"    element={<CaregiversPage />} />
        <Route path="visits"        element={<VisitsPage />} />
        <Route path="attendance"    element={<AttendancePage />} />
        <Route path="billing"       element={<BillingPage />} />
        <Route path="careplans"     element={<CarePlansPage />} />
        <Route path="payroll"       element={<PayrollPage />} />
        <Route path="training"      element={<TrainingPage />} />
        <Route path="documents"     element={<DocumentsPage />} />
        <Route path="medicaid"      element={<MedicaidPage />} />
        <Route path="users"         element={<UsersPage />} />
        <Route path="notifications" element={<NotificationsPage />} />

        {/* ── Client Portal routes ── */}
        <Route path="client/dashboard"   element={<ClientDashboard />} />
        <Route path="client/visits"      element={<ClientVisits />} />
        <Route path="client/caregivers"  element={<ClientCaregivers />} />
        <Route path="client/attendance"  element={<ClientAttendance />} />
        <Route path="client/careplan"    element={<ClientCarePlan />} />
      </Route>

      {/* Fallback — redirect to role-appropriate home */}
      <Route path="*" element={<Navigate to={homePath} replace />} />

    </Routes>
  );
};

// ── Root App ──────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="light"
        />
      </BrowserRouter>
    </AuthProvider>
  );
}